/**
 * focusAtoms - Source atoms for Focus Mode state.
 *
 * Focus Mode is entered by double-clicking a base node. It hides other base nodes,
 * centers the focused node, and allows exploring its relationships.
 *
 * Key concepts:
 * - **Focused node**: A base node that is "focused". Only base nodes can be focused.
 * - **Active node**: The node whose relationships are currently shown in the Explorer tab.
 *   Can be the focused node or a drilled-into child.
 * - **Navigation path**: The breadcrumb trail from focused node → ... → active node.
 * - **Selected relationships**: Toggled-on relationship pills displayed in the graph.
 */

import { atom, type Atom, type PrimitiveAtom } from 'jotai';
import type { ObjectNodeData } from '../nodes/ObjectNode';
import type { ExplorationDirection } from '../services/cypherQueryService';

// ============================================================================
// Types
// ============================================================================

/**
 * A selected relationship in focus mode. Tracks which node it originated from,
 * the relationship type, and the direction of traversal.
 */
export interface SelectedRelationship {
	/** Unique key: `${sourceNodeAri}:${direction}:${relType}` */
	key: string;
	/** The ARI of the node this relationship was explored from */
	sourceNodeAri: string;
	/** The display name of the source node */
	sourceNodeName: string;
	/** The relationship type (Cypher underscore format, may be a compound key for split rels) */
	relType: string;
	/** The direction of traversal */
	direction: ExplorationDirection;
	/** Pre-computed display type for edge labels (hyphenated, not compound key). Falls back to relType. */
	displayType?: string;
	/** Pre-computed display verb (e.g. "linked to"). */
	displayVerb?: string;
}

// ============================================================================
// Source atoms
// ============================================================================

/** The currently focused base node (null = not in focus mode) */
export const FocusedNodeAtom: PrimitiveAtom<ObjectNodeData | null> & {
	init: ObjectNodeData | null;
} = atom<ObjectNodeData | null>(null);

/**
 * The active exploration node — the node whose relationships are currently
 * shown in the Explorer tab. Can be a base node OR a child node.
 * When null in focus mode, defaults to the focused node (via derived atom).
 */
export const ActiveNodeAtom: PrimitiveAtom<ObjectNodeData | null> & {
	init: ObjectNodeData | null;
} = atom<ObjectNodeData | null>(null);

/**
 * The breadcrumb navigation path from focused node to active node.
 * Contains intermediate nodes only (the focused node and active node
 * are derived from their own atoms and shown at the start/end of the breadcrumb).
 */
export const NavigationPathAtom: PrimitiveAtom<ObjectNodeData[]> & {
	init: ObjectNodeData[];
} = atom<ObjectNodeData[]>([]);

/** Currently selected relationships (displayed as removable pills) */
export const SelectedRelationshipsAtom: PrimitiveAtom<SelectedRelationship[]> & {
	init: SelectedRelationship[];
} = atom<SelectedRelationship[]>([]);

/** The active exploration direction tab in the Explorer panel */
export const FocusDirectionAtom: PrimitiveAtom<ExplorationDirection> & {
	init: ExplorationDirection;
} = atom<ExplorationDirection>('outgoing');

// ============================================================================
// Derived atoms
// ============================================================================

/** Whether focus mode is active */
export const IsFocusModeAtom: Atom<boolean> = atom<boolean>((get) => {
	return get(FocusedNodeAtom) !== null;
});

/**
 * The effective exploration node — active node if set,
 * otherwise falls back to the focused node.
 */
export const EffectiveExplorationNodeAtom: Atom<ObjectNodeData | null> =
	atom<ObjectNodeData | null>((get) => {
		return get(ActiveNodeAtom) ?? get(FocusedNodeAtom);
	});

/** Whether we are drilled into a child and can navigate back to the focused node */
export const CanGoBackExplorationAtom: Atom<boolean> = atom<boolean>((get) => {
	const active = get(ActiveNodeAtom);
	const focused = get(FocusedNodeAtom);
	// Can go back if there's an active node different from the focused node,
	// even if the navigation path has no intermediates (direct child)
	if (active && focused && active.id !== focused.id) {
		return true;
	}
	return get(NavigationPathAtom).length > 0;
});

/** Whether the active node is different from the focused node (drilling into child) */
export const IsDrilledIntoChildAtom: Atom<boolean> = atom<boolean>((get) => {
	const focused = get(FocusedNodeAtom);
	const active = get(ActiveNodeAtom);
	if (!focused || !active) {
		return false;
	}
	return focused.id !== active.id;
});

/** Whether any relationships are currently selected */
export const HasSelectedRelationshipsAtom: Atom<boolean> = atom<boolean>((get) => {
	return get(SelectedRelationshipsAtom).length > 0;
});

/**
 * The relationship types currently selected for the effective exploration node.
 * Used to determine which relationships are highlighted in the list.
 */
export const SelectedRelTypesForActiveNodeAtom: Atom<Set<string>> = atom<Set<string>>((get) => {
	const explorationNode = get(EffectiveExplorationNodeAtom);
	const selectedRels = get(SelectedRelationshipsAtom);
	if (!explorationNode) {
		return new Set<string>();
	}
	return new Set(
		selectedRels
			.filter((r) => r.sourceNodeAri === explorationNode.id)
			.map((r) => `${r.direction}:${r.relType}`),
	);
});
